import { servicesData } from "../Components/serviceMetaData";
import { StoriesData } from "../Components/storiesMetaData";
import { voicesSolutionsMetadata } from "../voices/voicesSolutionsData";

// Current repository canonicals use www. Production host verification is pending;
// the conflicting apex sitemap is not evidence for a host migration.
const PUBLIC_ORIGIN = "https://www.antrazal.com";
const LEGACY_BASE = "/DjUcNcbvQ0PZ9KGjn3t6OEmC7lq5dO0ckborXyZVTnU0Gd5OPJMjpwLKC48QwHAqcXLWVBnXC5fFwMjNzCNGp5C8fcZ9U7friZIp";
// Non-public application/API namespaces. This metadata policy is not access control.
const PRIVATE_PREFIXES = ["/hrm", "/e", "/sow", "/admin", "/backend", LEGACY_BASE.toLowerCase()];

// Copy from the current page metadata, not the handoff's unapproved drafts.
const coreData = [
  {
    pathname: '/services',
    title: 'Services | Antrazal',
    description: 'Explore Antrazal services across Salesforce, enterprise AI, integrations, cloud and data.',
    source: 'voices/VoicesDirectory.js',
  },
  {
    pathname: '/work',
    title: 'Customer Stories | Antrazal',
    description: 'Explore Antrazal customer stories, implementation work and the teams behind it.',
    source: 'voices/VoicesDirectory.js',
  },
  {
    pathname: '/contact',
    title: 'Contact Antrazal',
    description: 'Tell Antrazal about your project, workflow or implementation needs.',
    source: 'voices/VoicesContact.js',
  },
  {
    pathname: '/site-map',
    title: 'Site Directory | Antrazal',
    description: 'Find Antrazal services, customer stories, articles and company information.',
    source: 'voices/VoicesDirectory.js',
  },
  {
    pathname: "/",
    title: "Business Consulting & AI Automation Company | Antrazal",
    description: "Antrazal Solutions is a Business consulting and AI automation company delivering cloud integration, MuleSoft, Snowflake, DevOps and enterprise digital transformation services.",
    source: "MainPage.js",
  },
  {
    pathname: "/who-we-are",
    title: "Who We Are | Business Consulting Company | Antrazal",
    description: "Antrazal is a leading business consulting company delivering exceptional Salesforce development services worldwide. Meet our expert team and learn how we build beyond limits.",
    source: "Components/About.js",
  },
  {
    pathname: "/careers",
    title: "Careers at Antrazal | Agentic AI and Enterprise Consulting Jobs",
    description: "Join Antrazal to build agentic AI workflows, enterprise automation, Salesforce solutions, and business transformation programs.",
    source: "pages/Careers.js",
  },
  {
    pathname: "/products/edi-converter",
    title: "Antrazal EDI Converter | Browser-only EDI to Excel, CSV and JSON",
    description: "Convert EDI files into Excel, CSV and JSON directly in your browser. No upload, no storage, no server processing.",
    source: "pages/EdiConverter.js",
  },
  {
    pathname: "/events",
    title: "Events | Antrazal",
    description: "Meet Antrazal at enterprise technology, community, and industry events in 2026.",
    source: "pages/Events.js",
  },
  {
    pathname: "/case-studies",
    title: "Case Studies & White Papers | Antrazal",
    description: "Antrazal case studies and practical white papers for governed Agentforce and enterprise AI operating models.",
    source: "pages/CaseStudiesLibrary.js",
  },
  {
    pathname: "/privacy-policy",
    title: "Privacy Policy | Antrazal",
    description: "This Privacy Policy explains how we collect, use, disclose, store, and protect personal data obtained from visitors to our website, users of our services, and individuals who communicate with us through any online or offline channel.",
    source: "Components/PrivacyAndPolicy.js",
  },
];

// Article.js keeps its one article in a local, non-exported metaData array.
// These are its existing heading and opening paragraph, without the React import.
const articleData = [
  {
    pathname: "/article/how-to-keep-your-salesforce-marketing-cloud-contacts-within-the-allowed-limit",
    title: "How to Keep your Salesforce Marketing Cloud Contacts within the Allowed Limit",
    description: "Keeping your databases relevant under the system defined limits is crucial to make best use of the data you have. And, when it comes to the data running through your organization's marketing funnels, it becomes equally important to maintain the rightness of the data as much as it is to cultivate it.",
    source: "Components/Article.js",
    type: "article",
  },
];

const ENTITIES = {
  amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: " ",
  ndash: "\u2013", mdash: "\u2014", lsquo: "\u2018", rsquo: "\u2019",
  ldquo: "\u201c", rdquo: "\u201d", hellip: "\u2026", copy: "\u00a9",
  reg: "\u00ae", trade: "\u2122",
};

// Metadata text only, not an HTML sanitizer. No DOM access keeps Node and browser
// output identical; callers must render these strings as text/attribute values.
function plainText(value) {
  if (typeof value !== "string") return "";
  return value
    .replace(/&(#x[\da-f]+|#\d+|[a-z]+);/gi, (entity, key) => {
      if (key[0] !== "#") return ENTITIES[key.toLowerCase()] || entity;
      const hex = key[1].toLowerCase() === "x";
      const code = parseInt(key.slice(hex ? 2 : 1), hex ? 16 : 10);
      if (code <= 0 || code > 0x10ffff || (code >= 0xd800 && code <= 0xdfff)) return " ";
      return String.fromCodePoint(code);
    })
    .replace(/<!--[\s\S]*?-->/g, " ")
    .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, " ")
    .replace(/<\/?(?:br|hr|p|div|li|h[1-6]|section|article|tr|td|ul|ol|blockquote)\b[^>]*>/gi, " ")
    .replace(/<\/?[a-z][^>]*>/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

function firstText(...values) {
  return values.map(plainText).find(Boolean) || "";
}

function brandedTitle(value) {
  const title = plainText(value).replace(/(?:\s*\|\s*Antrazal(?: Solutions)?)+$/gi, "").trim();
  if (!title) return "Antrazal";
  return /\bantrazal\b/i.test(title) ? title : `${title} | Antrazal`;
}

// Must match App.js/Sales.js, which use name rather than the optional data.slug.
export function serviceSlug(name) {
  return name.toLowerCase().trim().replace(/&/g, "and")
    .replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-");
}

/** Existing BlogD URL rule. Deliberately retain apostrophes and punctuation. */
export function blogSlug(title) {
  return typeof title === "string" ? title.toLowerCase().replace(/\s+/g, "-") : "";
}

// Encode source paths once. Keep existing apostrophes, ampersands and parentheses;
// question/hash characters in a CMS title are path data, not query/fragment state.
function encodePublicPath(pathname) {
  try {
    return encodeURI(pathname).replace(/\?/g, "%3F").replace(/#/g, "%23");
  } catch {
    return null;
  }
}

function normalizePath(pathname) {
  if (typeof pathname !== "string" || !pathname.startsWith("/") || pathname.startsWith("//")) return null;
  const path = pathname.split(/[?#]/, 1)[0];
  try {
    // Decode segments separately so an encoded slash cannot change route boundaries.
    const parts = path.split("/").map(decodeURIComponent);
    if (parts.some((part) => /[/\\]/.test(part))) return null;
    return parts.join("/").replace(/\/+$/, "") || "/";
  } catch {
    return null;
  }
}

const staticRecords = [
  ...coreData,
  ...voicesSolutionsMetadata.map(({ path, title, description }) => ({ pathname: path, title, description, source: 'voices/voicesSolutionsData.js' })),
  ...servicesData.filter((service) => service.name).map((service) => ({
    pathname: `/services/${serviceSlug(service.name)}`,
    title: `${service.name} Services | Business Consulting`,
    description: firstText(service.shortDescription, service.headline, service.name),
    source: "Components/serviceMetaData.js",
  })),
  ...StoriesData.filter((story) => story.url).map((story) => ({
    pathname: `/customer-stories/${story.url}`,
    title: `${firstText(story.headline, story.detailPageHeadline)} Customer Story`,
    description: firstText(story.description, story.detailPageHeadline, story.headline),
    source: "Components/storiesMetaData.js",
    type: "article",
    image: story.backgroundImage || null,
  })),
  ...articleData,
].map((record) => ({ ...record, pathname: encodePublicPath(record.pathname) }));
const staticByPath = new Map(staticRecords.map((record) => [normalizePath(record.pathname)?.toLowerCase(), record]));
const aliases = new Map([["/career", "/careers"], ["/edi-converter", "/products/edi-converter"]]);

function blogRecords(blogs) {
  if (!Array.isArray(blogs)) return [];
  return blogs.filter((blog) => plainText(blog?.title)).map((blog) => {
    const content = plainText(blog.content);
    let excerpt = content;
    if (content.length > 240) {
      excerpt = content.slice(0, 240).replace(/\s+\S*$/, "") + "...";
    }
    return {
      pathname: encodePublicPath(`/blog/${blogSlug(blog.title)}`),
      title: blog.title,
      description: firstText(blog.description, blog.shortDescription, excerpt, blog.title),
      source: "CMS blog payload",
      type: "article",
    };
  }).filter((record) => {
    const path = normalizePath(record.pathname);
    return path !== null && /^\/blog\/[^/]+$/.test(path) && !["/blog/.", "/blog/.."].includes(path);
  });
}

function publicImageUrl(value) {
  if (typeof value !== "string" || !value.trim()) return null;
  try {
    const url = new URL(value, PUBLIC_ORIGIN);
    if (!["http:", "https:"].includes(url.protocol) || url.username || url.password) return null;
    return url.href;
  } catch {
    return null;
  }
}

function publicResult(record) {
  return {
    status: "public",
    pathname: record.pathname,
    title: brandedTitle(record.title),
    description: plainText(record.description),
    canonical: PUBLIC_ORIGIN + record.pathname,
    robots: "index, follow",
    type: record.type || "website",
    image: publicImageUrl(record.image),
    schema: null,
    source: record.source,
  };
}

function nonPublicResult(status, pathname) {
  const pending = status === "pending";
  let title = "Page Not Found";
  if (pending) title = "Article";
  if (status === "private") title = "Private Page";
  return {
    status,
    pathname,
    title: brandedTitle(title),
    description: "",
    canonical: null,
    robots: pending ? null : "noindex, nofollow",
    type: null,
    image: null,
    schema: null,
  };
}

/**
 * Pure route metadata: no fetching, DOM writes, schema claims, or redirects.
 * status: public | private | notFound | pending | redirect.
 * Null canonical/schema/robots means OMIT that head field, not a home fallback.
 * Matching is case-insensitive like the router; canonical record paths are encoded
 * once and keep source case. blogSlug returns the original, unencoded slug.
 * Queries/fragments are UI state, including /careers?application=; aliases return
 * redirectTo but are not inventory entries. blogsLoading/blogsError keep an
 * absent CMS article pending until a successful payload confirms it is missing.
 * The historic /story/1 has no established destination and remains notFound.
 */
export function resolvePublicSeo(pathname, { blogs = [], blogsLoading = false, blogsError = false } = {}) {
  const path = normalizePath(pathname);
  if (path === null) return nonPublicResult("notFound", null);
  const lowerPath = path.toLowerCase();
  if (PRIVATE_PREFIXES.some((prefix) => lowerPath === prefix || lowerPath.startsWith(`${prefix}/`))) {
    return nonPublicResult("private", path);
  }
  const record = staticByPath.get(lowerPath);
  if (record) return publicResult(record);
  if (aliases.has(lowerPath)) {
    return {
      ...nonPublicResult("redirect", path),
      title: brandedTitle(staticByPath.get(aliases.get(lowerPath)).title),
      redirectTo: aliases.get(lowerPath),
    };
  }
  if (/^\/blog\/[^/]+$/.test(lowerPath)) {
    const blog = blogRecords(blogs).find((item) => normalizePath(item.pathname)?.toLowerCase() === lowerPath);
    if (blog) return publicResult(blog);
    if (blogsLoading || blogsError || !Array.isArray(blogs)) return nonPublicResult("pending", path);
  }
  return nonPublicResult("notFound", path);
}

/**
 * Unique current public metadata records, optionally including discovered CMS
 * blogs. Inventory only, NOT an approved sitemap. Approval/verification flags
 * remain false; matching existing copy is not evidence of claim approval.
 */
export function getPublicSeoRoutes(blogs = []) {
  const records = new Map();
  [...staticRecords, ...blogRecords(blogs)].forEach((record) => {
    if (!records.has(record.pathname)) records.set(record.pathname, record);
  });
  return Array.from(records.values(), (record) => ({
    ...publicResult(record),
    metadataApproved: false,
    schemaApproved: false,
    canonicalHostVerified: false,
    productionVerified: false,
    sitemapEligible: false,
  }));
}
